import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { getVersion } from "@/lib/api";

function formatBuildTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * BuildBadge — a small pill showing the current backend version and when
 * the running container started. Lets the user instantly confirm a fresh
 * deploy landed without digging through the Emergent Deployments panel.
 */
export default function BuildBadge() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    getVersion()
      .then((d) => { if (alive) setInfo(d); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, []);

  if (error) {
    return (
      <span
        data-testid="build-badge"
        className="inline-flex items-center gap-2 border border-rose-600 bg-white px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-rose-700"
        title="Couldn't reach backend"
      >
        BACKEND UNREACHABLE
      </span>
    );
  }

  if (!info) {
    return (
      <span
        data-testid="build-badge"
        className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking…
      </span>
    );
  }

  const built = formatBuildTime(info.build_started_at);
  const rel = timeAgo(info.build_started_at);

  return (
    <span
      data-testid="build-badge"
      title={`Backend built ${built} · deployed ${rel}`}
      className="inline-flex items-center gap-2 border border-zinc-950 bg-zinc-950 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white"
    >
      <CheckCircle2 className="h-3 w-3 text-emerald-400" />
      Live · v{info.version} · Deployed {rel}
    </span>
  );
}
